import { useState, useEffect } from 'react';
import { useLocation, Link } from 'wouter';
import { LayoutDashboard, Menu, X, ArrowRight } from 'lucide-react';
import { trackMarketingEvent } from '../../lib/analytics';

const NAV_LINKS = [
    { href: '/product', label: 'Product' },
    { href: '/how-it-works', label: 'How It Works' },
    { href: '/solutions', label: 'Solutions' },
    { href: '/pricing', label: 'Pricing' },
    { href: '/faq', label: 'FAQ' },
    { href: '/contact', label: 'Contact' },
];

const MarketingNav = ({ variant = 'light' }: { variant?: 'light' | 'dark' }) => {
    const [location, setLocation] = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const isLight = variant === 'light';

    // Close mobile menu when route changes
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location]);

    // Close mobile menu on Escape key
    useEffect(() => {
        if (!mobileMenuOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMobileMenuOpen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [mobileMenuOpen]);

    return (
        <header
            className={`border-b backdrop-blur-md sticky top-0 z-50 transition-colors ${
                isLight ? 'border-slate-200/90 bg-white/95' : 'border-slate-800 bg-slate-950/95'
            }`}
        >
            <nav
                aria-label="Primary Navigation"
                className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3"
            >
                {/* Brand + Desktop Links */}
                <div className="flex items-center gap-6 min-w-0">
                    <Link
                        href="/"
                        className={`flex items-center gap-2.5 font-extrabold text-xl tracking-tight shrink-0 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded-lg ${
                            isLight ? 'text-slate-900' : 'text-white'
                        }`}
                    >
                        <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center text-white shadow-sm shadow-teal-600/25">
                            <LayoutDashboard size={17} />
                        </div>
                        <span>BayWhiz</span>
                    </Link>

                    <div className="hidden lg:flex items-center gap-1">
                        {NAV_LINKS.map((item) => {
                            const isActive = location === item.href;
                            return (
                                <Link
                                    key={item.href}
                                    href={item.href}
                                    aria-current={isActive ? 'page' : undefined}
                                    className={`text-sm font-medium transition-colors px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                                        isLight
                                            ? isActive
                                                ? 'text-teal-700 bg-teal-50 font-semibold'
                                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                                            : isActive
                                              ? 'text-white bg-slate-900 font-semibold'
                                              : 'text-slate-300 hover:text-white hover:bg-slate-900'
                                    }`}
                                >
                                    {item.label}
                                </Link>
                            );
                        })}
                    </div>
                </div>

                {/* Right side actions + Mobile Menu Toggle */}
                <div className="flex items-center gap-2 shrink-0">
                    <Link
                        href="/login?mode=warehouse"
                        className={`hidden md:inline-flex items-center text-sm font-medium transition-colors px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                            isLight
                                ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                                : 'text-slate-300 hover:text-white hover:bg-slate-900'
                        }`}
                    >
                        Warehouse Login
                    </Link>
                    <Link
                        href="/carrier-login"
                        className={`hidden xl:inline-flex items-center text-sm font-medium transition-colors px-2.5 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                            isLight
                                ? 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                                : 'text-slate-400 hover:text-white hover:bg-slate-900'
                        }`}
                    >
                        Carrier Login
                    </Link>

                    <button
                        type="button"
                        onClick={() => {
                            trackMarketingEvent('start_trial_click', {
                                event_category: 'engagement',
                                event_label: 'navbar_start_trial',
                            });
                            setLocation('/request-trial');
                        }}
                        className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-all px-3.5 sm:px-4 py-2 rounded-lg shadow-sm shadow-teal-600/20 whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                    >
                        <span className="sm:hidden">Request Free Trial</span>
                        <span className="hidden sm:inline">Request Free Trial</span>
                        <ArrowRight size={14} className="hidden sm:inline" />
                    </button>

                    <button
                        type="button"
                        onClick={() => setMobileMenuOpen((prev) => !prev)}
                        aria-expanded={mobileMenuOpen}
                        aria-controls="marketing-mobile-menu"
                        aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                        className={`lg:hidden inline-flex items-center justify-center p-2 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                            isLight
                                ? 'border-slate-200 text-slate-700 hover:bg-slate-100'
                                : 'border-slate-800 text-slate-200 hover:bg-slate-900'
                        }`}
                    >
                        {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </nav>

            {/* Mobile Navigation Drawer */}
            {mobileMenuOpen && (
                <div
                    id="marketing-mobile-menu"
                    role="region"
                    aria-label="Mobile Navigation"
                    className={`lg:hidden border-t px-4 pt-3 pb-5 space-y-3 shadow-lg ${
                        isLight ? 'bg-white border-slate-200 text-slate-900' : 'bg-slate-950 border-slate-800 text-white'
                    }`}
                >
                    <div className="grid grid-cols-1 gap-1">
                        {NAV_LINKS.map((item) => {
                            const isActive = location === item.href;
                            return (
                                <Link
                                    key={item.href}
                                    href={item.href}
                                    onClick={() => setMobileMenuOpen(false)}
                                    aria-current={isActive ? 'page' : undefined}
                                    className={`block px-3 py-2.5 rounded-lg text-base font-medium transition-colors ${
                                        isLight
                                            ? isActive
                                                ? 'bg-teal-50 text-teal-700 font-semibold'
                                                : 'text-slate-700 hover:bg-slate-100'
                                            : isActive
                                              ? 'bg-slate-900 text-teal-400 font-semibold'
                                              : 'text-slate-200 hover:bg-slate-900'
                                    }`}
                                >
                                    {item.label}
                                </Link>
                            );
                        })}
                    </div>

                    <div className={`pt-3 border-t flex flex-col gap-2 ${isLight ? 'border-slate-200' : 'border-slate-800'}`}>
                        <div className="grid grid-cols-2 gap-2">
                            <Link
                                href="/login?mode=warehouse"
                                onClick={() => setMobileMenuOpen(false)}
                                className={`text-center py-2.5 px-3 rounded-lg border text-sm font-semibold transition-colors ${
                                    isLight
                                        ? 'border-slate-300 text-slate-700 hover:bg-slate-50'
                                        : 'border-slate-700 text-slate-200 hover:bg-slate-900'
                                }`}
                            >
                                Warehouse Login
                            </Link>
                            <Link
                                href="/carrier-login"
                                onClick={() => setMobileMenuOpen(false)}
                                className={`text-center py-2.5 px-3 rounded-lg border text-sm font-semibold transition-colors ${
                                    isLight
                                        ? 'border-slate-300 text-slate-700 hover:bg-slate-50'
                                        : 'border-slate-700 text-slate-200 hover:bg-slate-900'
                                }`}
                            >
                                Carrier Login
                            </Link>
                        </div>

                        <button
                            type="button"
                            onClick={() => {
                                setMobileMenuOpen(false);
                                setLocation('/request-trial');
                            }}
                            className="w-full py-3 px-4 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm shadow-sm flex items-center justify-center gap-2"
                        >
                            Request Free Trial
                            <ArrowRight size={16} />
                        </button>
                        <p className="text-center text-xs text-slate-500">
                            30-day full-access trial · No credit card required
                        </p>
                    </div>
                </div>
            )}
        </header>
    );
};

export default MarketingNav;
